import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Linkedin, Mail, ExternalLink } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AboutPage = () => {
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Pratish Jain',
    jobTitle: 'Content Writer & SEO Strategist',
    url: 'https://pratishjain.com',
    sameAs: [
      'https://linkedin.com/in/pratishjain',
    ],
  };

  return (
    <>
      <Helmet>
        <title>About Searchefy — Built by Pratish Jain</title>
        <meta
          name="description"
          content="Searchefy was built by Pratish Jain, an SEO content writer who got tired of solving the same backlink problem again and again."
        />
        <link rel="canonical" href="https://searchefy.com/about" />
        <meta property="og:title" content="About Searchefy — Built by Pratish Jain" />
        <meta
          property="og:description"
          content="The story behind Searchefy — a tool born from daily workflow frustration."
        />
        <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
      </Helmet>

      <div data-testid="about-page" className="min-h-screen bg-[#F9FAFB]">
        <Navbar />

        <main className="pt-24">
          {/* Hero Section */}
          <section className="section-dark py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Image */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  className="order-2 lg:order-1"
                >
                  <div className="relative">
                    <div className="aspect-square max-w-md mx-auto rounded-2xl overflow-hidden bg-gradient-to-br from-blue-500/20 to-purple-500/20">
                      <img
                        src="https://images.unsplash.com/photo-1769071166862-8cc3a6f2ac5c?crop=entropy&cs=srgb&fm=jpg&q=85&w=500"
                        alt="Pratish Jain - Founder of Searchefy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Decorative elements */}
                    <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl -z-10"></div>
                  </div>
                </motion.div>

                {/* Content */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  className="order-1 lg:order-2"
                >
                  <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6">
                    Built by an SEO Writer Who Was Done with{' '}
                    <span className="gradient-text">Backlink Chaos</span>
                  </h1>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Story Section */}
          <section className="py-24 md:py-32">
            <div className="max-w-3xl mx-auto px-6 md:px-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="prose prose-lg max-w-none"
              >
                <p className="text-xl text-gray-700 leading-relaxed mb-8">
                  I'm <strong>Pratish Jain</strong>, an SEO content writer and content strategist who got tired of solving the same backlink problem again and again.
                </p>

                <p className="text-gray-600 leading-relaxed mb-6">
                  Every time I published a blog, the next task was obvious: build backlinks so it gets indexed faster, ranks better, and improves its chances of showing up across search, AEO, and GEO.
                </p>

                <p className="text-gray-600 leading-relaxed mb-6">
                  <strong className="text-gray-900">The problem?</strong> Good free backlink opportunities are painfully hard to find.
                </p>

                <p className="text-gray-600 leading-relaxed mb-6">
                  Most lists online are outdated, spammy, or full of low-quality sites. Finding genuinely useful directories with high DA, solid PA, and low spam scores used to take hours of manual research.
                </p>

                <p className="text-gray-600 leading-relaxed mb-6">
                  <strong className="text-gray-900">So I started curating my own private list.</strong>
                </p>

                <p className="text-gray-600 leading-relaxed mb-6">
                  Over time, another problem showed up. Managing dozens of backlink opportunities for multiple clients and multiple published blogs inside spreadsheets became a mess. Tracking what was submitted, what was pending, what worked, and what needed follow-up turned into an unnecessary headache.
                </p>

                <div className="my-12 p-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-100">
                  <p className="text-xl font-display font-bold text-gray-900 mb-4">
                    That's why Searchefy exists.
                  </p>
                  <p className="text-gray-600">
                    It started as a directory I built for myself as a content writer. Today, it's designed to help SEO writers, marketers, agencies, and founders find, manage, and scale quality backlink opportunities without the spreadsheet chaos.
                  </p>
                </div>

                <p className="text-gray-600 leading-relaxed mb-6">
                  <strong className="text-gray-900">Simple idea. Real problem. Built from daily workflow frustration.</strong>
                </p>

                <p className="text-gray-600 leading-relaxed">
                  If you've ever published a great piece of content and then struggled with "where do I build backlinks for this now?" — this was made for you.
                </p>
              </motion.div>

              {/* Founder Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-16 p-8 bg-white rounded-2xl border border-gray-200 shadow-sm"
              >
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                    PJ
                  </div>
                  <div className="text-center sm:text-left">
                    <h3 className="font-display text-xl font-bold text-gray-900">
                      Pratish Jain
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Content Writer & SEO Strategist
                    </p>
                    <div className="flex items-center justify-center sm:justify-start gap-4">
                      <a
                        href="https://linkedin.com/in/pratishjain"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="about-linkedin"
                        className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
                      >
                        <Linkedin size={18} />
                        LinkedIn
                      </a>
                      <a
                        href="mailto:thepratishjain@gmail.com"
                        data-testid="about-email"
                        className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
                      >
                        <Mail size={18} />
                        Email
                      </a>
                      <a
                        href="https://pratishjain.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="about-website"
                        className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
                      >
                        <ExternalLink size={18} />
                        Website
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="mt-12 text-center"
              >
                <a
                  href="https://checkout.dodopayments.com/buy/pdt_0Nc1Sd7RjzAPh38CNHxE9?quantity=1"
                  data-testid="about-cta"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Explore the Directory — $9 Lifetime
                </a>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default AboutPage;
